// Template references in promptInput text: {input} (edge), {name} (API param),
// {nodeId(.handle)} (another node's output). Shared by the engine, executor, and selector.

import { type Node, type Edge } from '@xyflow/react';

const REF_SRC = '\\{([A-Za-z][\\w-]*)(?:\\.([A-Za-z]\\w*))?\\}';
const refRegex = () => new RegExp(REF_SRC, 'g');

/** Map node ids and titles → node id (for resolving {ref} tags). */
function refIndex(nodes: Node[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const n of nodes) {
    m.set(n.id, n.id);
    const t = (n.data as { title?: string })?.title;
    if (t) m.set(String(t), n.id);
  }
  return m;
}

/** Implicit ordering edges from promptInput {ref} references (topo-sort only). */
export function refDependencyEdges(nodes: Node[]): Edge[] {
  const idx = refIndex(nodes);
  const extra: Edge[] = [];
  for (const n of nodes) {
    if (n.type !== 'promptInput') continue;
    const text = String((n.data as { text?: string })?.text ?? '');
    const re = refRegex();
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const base = m[1];
      const src = base === 'input' ? undefined : idx.get(base);
      if (src && src !== n.id) {
        extra.push({ id: `ref-${src}-${n.id}`, source: src, target: n.id });
      }
    }
  }
  return extra;
}

/** Resolve {input} (edge) and {nodeId(.handle)} (node output) tags; unknown tags stay literal. */
export function resolveText(
  text: string,
  socketInputs: Record<string, unknown>,
  outputs: Record<string, Record<string, unknown>>,
): string {
  return text.replace(refRegex(), (full, base: string, handle?: string) => {
    if (base === 'input' && !handle) return String(socketInputs.input ?? '');
    const o = outputs[base];
    if (o) {
      const v = handle ? o[handle] : o.text; // bare ref = text output only (no base64)
      if (v == null || typeof v === 'object') return '';
      return String(v);
    }
    return full;
  });
}

/** Other text-producing nodes that can be referenced (for the insert-reference selector). */
export function referenceCandidates(
  nodes: Node[],
  selfId: string,
): Array<{ id: string; ref: string; label: string; preview: string }> {
  const TEXT_TYPES = new Set(['promptInput', 'textInput', 'caption', 'textJoin']);
  return nodes
    .filter((n) => n.id !== selfId && TEXT_TYPES.has(n.type ?? ''))
    .map((n) => {
      const d = n.data as { text?: string; title?: string };
      const label =
        n.type === 'caption'
          ? 'Caption'
          : n.type === 'textJoin'
            ? 'Text Join'
            : n.type === 'textInput'
              ? 'Text'
              : 'Prompt';
      // Use the title only if it's a valid (space-free) ref token; else the id.
      const safeTitle = d.title && /^[A-Za-z][\w-]*$/.test(d.title) ? d.title : null;
      return {
        id: n.id,
        ref: safeTitle || n.id,
        label,
        preview: String(d.text ?? '').slice(0, 40),
      };
    });
}

/** If the caret sits inside an unclosed `{…` token, return its start index + partial query. */
export function activeRefToken(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const open = upto.lastIndexOf('{');
  if (open === -1) return null;
  if (upto.lastIndexOf('}') > open) return null; // the brace is already closed
  const query = upto.slice(open + 1);
  if (!/^[A-Za-z0-9_.\- ]*$/.test(query)) return null; // not a token context
  return { start: open, query };
}
